import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PROBLEM_CODES, SIGNUP_STATUS_LABELS, api, errorMessage, problemCode, useApiMutation, useApiQuery } from "../../api";
import type { Signup } from "../../api";
import { Badge, Card, ConfirmDialog, EmptyState, Table, useToast } from "../../components";
import type { TableColumn } from "../../components";
import { formatDateTime } from "../../format";
import { AsyncBoundary, PageHeader, esnEntreprisePath } from "../../shell";
import styles from "./Esn.module.css";
import { EsnInscriptionDetail } from "./EsnInscriptionDetail";
import { TON_STATUT } from "./inscriptionStatut";
import { InscriptionsFiltres } from "./InscriptionsFiltres";
import type { StatutInscriptions } from "./InscriptionsFiltres";
import { RefusInscriptionModal } from "./RefusInscriptionModal";

/**
 * La file des demandes d'inscription. Accepter crée l'entreprise et invite le
 * contact comme premier administrateur — c'est le serveur qui fait les deux
 * dans une même transaction ; refuser conserve un motif interne.
 */
export function EsnInscriptionsPage() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [statut, setStatut] = useState<StatutInscriptions>("pending");
  const demandes = useApiQuery(
    (o) => api.listSignups(statut === "toutes" ? {} : { status: statut }, o),
    [statut],
  );
  const [detail, setDetail] = useState<Signup | null>(null);
  const [aAccepter, setAAccepter] = useState<Signup | null>(null);
  const [aRefuser, setARefuser] = useState<Signup | null>(null);

  const acceptation = useApiMutation((id: string) => api.acceptSignup(id));
  const refus = useApiMutation((id: string, reason: string) => api.rejectSignup(id, { reason }));

  async function accepter() {
    if (aAccepter === null) {
      return;
    }
    const res = await acceptation.run(aAccepter.id);
    if (res === null) {
      setAAccepter(null);
      return;
    }
    setAAccepter(null);
    if (!res.invited.mail_sent) {
      notify({
        title: "Entreprise créée, courriel non parti",
        description: `Le courriel d'invitation de ${res.invited.user.email} n'a pas pu partir. Renvoyez-le depuis la fiche.`,
        tone: "warning",
        duration: 0,
      });
    } else {
      notify({ title: `${res.company.name} créée`, description: `${res.invited.user.email} est invité comme administrateur.`, tone: "success" });
    }
    navigate(esnEntreprisePath(res.company.id));
  }

  async function refuser(motif: string) {
    if (aRefuser === null) {
      return;
    }
    const res = await refus.run(aRefuser.id, motif);
    if (res === null) {
      return;
    }
    setARefuser(null);
    notify({ title: `Demande de ${res.company_name} refusée`, tone: "neutral" });
    demandes.reload();
  }

  // Les refus du serveur, mis en mots ici plutôt que dans chaque dialogue.
  const codeAcceptation = problemCode(acceptation.error);
  const erreurAcceptation =
    acceptation.error === null
      ? null
      : codeAcceptation === PROBLEM_CODES.signupAlreadyDecided
        ? "Cette demande a déjà été traitée par quelqu'un d'autre. La liste a été relue."
        : codeAcceptation === PROBLEM_CODES.emailTaken
          ? "Un compte porte déjà cette adresse : refusez la demande en le disant, ou invitez la personne depuis l'entreprise existante."
          : errorMessage(acceptation.error);
  if (erreurAcceptation !== null && aAccepter === null && acceptation.error !== null) {
    // Affiché une fois, puis l'état de la mutation est remis à zéro pour ne
    // pas le réafficher au prochain rendu.
    notify({ title: "Acceptation impossible", description: erreurAcceptation, tone: "danger", duration: 0 });
    acceptation.reset();
    if (codeAcceptation === PROBLEM_CODES.signupAlreadyDecided) {
      demandes.reload();
    }
  }
  const codeRefus = problemCode(refus.error);
  const erreurRefus =
    refus.error === null
      ? null
      : codeRefus === PROBLEM_CODES.signupAlreadyDecided
        ? "Cette demande a déjà été traitée."
        : errorMessage(refus.error);

  const colonnes = useMemo<TableColumn<Signup>[]>(
    () => [
      { key: "entreprise", header: "Entreprise", render: (s) => <strong>{s.company_name}</strong> },
      {
        key: "contact",
        header: "Contact",
        render: (s) => (
          <span>
            {s.contact_name}
            <span className={styles.contactSecondaire}> · {s.contact_title}</span>
          </span>
        ),
      },
      { key: "email", header: "E-mail", render: (s) => s.email },
      { key: "telephone", header: "Téléphone", render: (s) => s.phone },
      { key: "recue", header: "Reçue le", render: (s) => formatDateTime(s.created_at) },
      {
        key: "statut",
        header: "Statut",
        render: (s) => (
          <Badge tone={TON_STATUT[s.status]} dot size="sm">
            {SIGNUP_STATUS_LABELS[s.status]}
          </Badge>
        ),
      },
    ],
    [],
  );

  const vide =
    statut === "pending"
      ? { titre: "Aucune demande en attente", texte: "Les demandes déposées depuis la page d'accueil arrivent ici." }
      : { titre: "Aucune demande", texte: "Rien dans cet état pour le moment." };

  return (
    <>
      <PageHeader
        eyebrow="Espace ESN"
        title="Demandes d'inscription"
        description="Les entreprises qui demandent un compte depuis la page d'accueil. Accepter crée l'entreprise et invite le contact comme administrateur ; refuser conserve un motif interne."
      />

      <InscriptionsFiltres valeur={statut} onChange={setStatut} />

      <AsyncBoundary
        state={demandes}
        errorTitle="Demandes illisibles"
        skeleton={<Table columns={colonnes} rows={[]} rowKey={(s) => s.id} caption="Demandes d'inscription" loading animateRows={false} />}
      >
        {(donnees) => (
          <Card padding="none">
            <Table
              columns={colonnes}
              rows={donnees.signups}
              rowKey={(s) => s.id}
              caption="Demandes d'inscription"
              animateRows={false}
              onRowClick={(s) => setDetail(s)}
              empty={<EmptyState compact title={vide.titre} description={vide.texte} />}
            />
          </Card>
        )}
      </AsyncBoundary>

      <EsnInscriptionDetail
        signup={detail}
        onClose={() => setDetail(null)}
        onAccept={(s) => {
          setDetail(null);
          setAAccepter(s);
        }}
        onReject={(s) => {
          setDetail(null);
          refus.reset();
          setARefuser(s);
        }}
      />

      <ConfirmDialog
        open={aAccepter !== null}
        title="Accepter cette demande"
        description={
          aAccepter === null
            ? ""
            : `L'entreprise « ${aAccepter.company_name} » sera créée avec son portefeuille, et ${aAccepter.email} recevra une invitation comme administrateur.`
        }
        confirmLabel="Accepter et inviter"
        pending={acceptation.pending}
        onConfirm={() => void accepter()}
        onCancel={() => setAAccepter(null)}
      />

      <RefusInscriptionModal
        key={aRefuser?.id ?? "aucun"}
        open={aRefuser !== null}
        pending={refus.pending}
        erreur={erreurRefus}
        onConfirm={(motif) => void refuser(motif)}
        onCancel={() => setARefuser(null)}
      />
    </>
  );
}
